<script setup lang="ts">
import { maskito as vMaskito } from '@maskito/vue'
import { MtdButton, MtdField, type MtdFieldProps } from '@paygine/mtd-uikit'
import { useRegle } from '@regle/core'
import { minLength, required, withMessage } from '@regle/rules'
import { computed, ref, watch } from 'vue'

import { ArrowIcon, GlobusIcon, RussianIcon } from '@/shared/icons'
import { nonStandartCarNumberMask, standartCarNumberMask } from '@/shared/lib/masks'
import { VSearchableList } from '@/shared/ui'

import { ALLOWED_CHARACTERS_MESSAGE, carNumberVariantSet, defaultNumberVariants } from '../config'
import { allowedCharactersValidator } from '../lib'
import { type CarNumberVariant, type ExternalError, type GrnCountry, useModel } from '../model'
import VPlaceholder from './VPlaceholder.vue'

const isOpen = ref(false)

const currentVariant = ref<CarNumberVariant>('RUS_STANDARD')
const currentCountry = ref<GrnCountry>('RUS')

const {
    enableVariantsSelect = true,
    externalError = {},
    fieldProps,
} = defineProps<{
    enableVariantsSelect?: boolean
    externalError?: ExternalError
    fieldProps?: MtdFieldProps
}>()

const { countries } = useModel()

const rules = computed(() => ({
    grn: {
        ...(currentVariant.value !== 'RUS_STANDARD' && {
            allowedCharacters: withMessage(allowedCharactersValidator, ALLOWED_CHARACTERS_MESSAGE),
        }),

        minLength: withMessage(
            minLength(currentVariant.value === 'RUS_STANDARD' ? 11 : 1),
            'Введите номер полностью'
        ),

        required: withMessage(required, 'Поле обязательно'),
    },
}))

const { r$ } = useRegle({ grn: '' }, rules, { autoDirty: false })

const emit = defineEmits<{
    'carNumber:update': [data: { country: string; grn: string }]
}>()

defineExpose({
    validate: async () => {
        const { valid } = await r$.$validate()
        return valid && !externalError.alreadyLinkedPhone && !externalError.unknownError
    },
})

const serVerErrorMessage = computed(() =>
    externalError.alreadyLinkedPhone
        ? `Этот номер уже используется клиентом с номером ${externalError.alreadyLinkedPhone}, абоненту необходимо сначала отвязать номер от указанного аккаунта`
        : ''
)

watch([() => r$.$value.grn, currentCountry], ([grn, country]) => {
    emit('carNumber:update', {
        country,
        grn,
    })
})

function selectNumberType(value: string) {
    if (carNumberVariantSet.has(value as CarNumberVariant)) {
        // выбран ВИД номера
        currentVariant.value = value as CarNumberVariant

        if (value !== 'OTHER') {
            currentCountry.value = 'RUS'
        }

        if (value === 'RUS_STANDARD') {
            r$.$value.grn = ''
        }
    } else {
        // выбрана СТРАНА
        currentCountry.value = value as GrnCountry

        //если Россия выбрана из списка c бэка, устанавливаем стандартный номер РФ
        if (value === 'RUS') {
            r$.$value.grn = ''
            currentVariant.value = 'RUS_STANDARD'
        } else {
            currentVariant.value = 'OTHER'
        }
    }
}

const fieldMessage = computed<string | undefined>(() => {
    if (
        !r$.grn.$errors[0] &&
        !serVerErrorMessage.value &&
        currentVariant.value !== 'RUS_STANDARD'
    ) {
        return ALLOWED_CHARACTERS_MESSAGE
    }

    return r$.grn.$errors[0] || serVerErrorMessage.value
})

const carNumberMask = computed(() => {
    if (currentVariant.value === 'RUS_STANDARD') return standartCarNumberMask
    return nonStandartCarNumberMask
})
</script>

<template>
    <MtdField
        v-model="r$.grn.$value"
        v-maskito="carNumberMask"
        :has-error="
            r$.grn.$error || !!externalError.alreadyLinkedPhone || externalError.unknownError
        "
        :message="fieldMessage"
        :maxlength="currentVariant !== 'RUS_STANDARD' ? 12 : undefined"
        :placeholder="currentVariant === 'RUS_NON_STANDARD' ? 'Госномер до 12 знаков' : undefined"
        v-bind="fieldProps"
        @blur="r$.grn.$touch()"
    >
        <template #startIcon>
            <MtdButton
                variant="main-flat"
                class="select-control"
                :style="{
                    pointerEvents: !enableVariantsSelect ? 'none' : 'auto',
                }"
                @click="isOpen = !isOpen"
            >
                <div class="select-control__content">
                    <RussianIcon v-if="currentVariant !== 'OTHER'" />
                    <GlobusIcon v-else />
                    <ArrowIcon
                        v-if="enableVariantsSelect"
                        :class="[
                            'select-control__arrow',
                            {
                                _open: isOpen,
                            },
                        ]"
                    />
                </div>
            </MtdButton>

            <VSearchableList
                v-model="isOpen"
                :empty="{
                    subtitle: 'Проверьте название страны',
                    title: 'Ничего не найдено',
                }"
                title="Выберите страну"
                :alphabetical="true"
                :items="[...(defaultNumberVariants || []), ...(countries || [])]"
                @select="({ id }) => selectNumberType(String(id))"
            />
        </template>
        <template
            v-if="currentVariant === 'RUS_STANDARD'"
            #endIcon
        >
            <VPlaceholder
                placeholder="А 000 АА 000"
                :length="r$.grn.$value?.length"
            />
        </template>
    </MtdField>
</template>

<style lang="scss" src="./VCarNumber.scss"></style>
